<template>
  <sub-section
    title="Any other income"
    helpTitle="Any other income"
    :helpText="HelpText.otherIncome()"
  >
    <div
      v-for="otherIncome in getOtherIncome"
      v-bind:key="otherIncome.id"
      class="row mb-2"
    >
      <div class="col-9 d-flex align-items-center">
        {{ otherIncome.description }}
      </div>
      <div class="col-3">
        <three-dot-menu
          :id="otherIncome.id"
          :permissions="getPermissionSet(otherIncome.editMode, true, true, true)"
          :aria-purpose="otherIncome.name"
          @view-clicked="onView"
          @editClicked="onEdit"
          @deleteClicked="onDelete"
        />
      </div>
    </div>

    <div v-show="!hasOtherIncome">
        <empty-asset name="other income" />
    </div>

    <div class="row mt-4">
      <add-asset
        name="income"
        longName="any other income"
        @addClicked="onAdd()"
        ref="addAssetComponent" 
      />
    </div>
  </sub-section>

  <modal :title="modalTitle" ref="assetModal" idKey="otherIncome" close-button-aria-label="Close other income popup editor" :returnFocusTo="addAssetButton">
    <other-income-editor ref="assetEditor" @edit-finished="onEditFinished" />
  </modal>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import SubSection from "@/guiide/components/common/SubSection.vue";
import HelpText from "@/guiide/ts/helpText";
import ThreeDotMenu from "@/guiide/components/common/ThreeDotMenu.vue";
import AddAsset from "@/guiide/components/assets/AddAsset.vue";
import EmptyAsset from "@/guiide/components/assets/EmptyAsset.vue";
import Modal from "@/guiide/components/common/Modal.vue";
import OtherIncomeEditor from "@/guiide/components/assets/OtherIncomeEditor.vue";
import ObjectBuilder from "@/guiide/ts/objectBuilder";
import Constants from "@/guiide/ts/constants";
import { app } from "@/guiide/ts/app";
import { OtherIncome } from "@/guiide/ts/otherIncome";
import TemporalDetail from "@/guiide/ts/temporalDetail";
import { getPermissionSet } from "@/guiide/ts/utilities";

export default defineComponent({
  name: "OtherIncomeViewer",
  components: {
    SubSection,
    ThreeDotMenu,
    AddAsset,
    EmptyAsset,
    Modal,
    OtherIncomeEditor
  },
  setup() {
    const assetModal = ref(null);
    const assetEditor = ref(null);

    return {
      assetModal,
      assetEditor,
      HelpText,
      getPermissionSet
    };
  },
  data() {
    return {
      modalTitle: "Edit other income",
      addAssetButton: ref<HTMLElement>(null)
    };
  },
  methods: {
    onAdd() {
      this.modalTitle = "Add other income";

      const defaultPaidFromDate = TemporalDetail.getOtherIncomeDefaultPaidFromDate();
      const defaultPaidToDate = TemporalDetail.getOtherIncomeDefaultPaidToDate();

      const incPaidFromMonthToAlignWithIncorrectMonthYearIndexing = defaultPaidFromDate.getMonth() + 1;
      const incPaidToMonthToAlignWithIncorrectMonthYearIndexing = defaultPaidToDate.getMonth() + 1;

      const otherIncome = ObjectBuilder.createOtherIncome(
        Constants.NewId,
        "",
        0,
        incPaidFromMonthToAlignWithIncorrectMonthYearIndexing,
        defaultPaidFromDate.getFullYear(),
        incPaidToMonthToAlignWithIncorrectMonthYearIndexing,
        defaultPaidToDate.getFullYear(),
        false
      );
      this.assetEditor.setValues(otherIncome, this.getRetirementYear());

      const addAssetComponent = this.$refs.addAssetComponent as any;
      this.addAssetButton = addAssetComponent.getAddButton();

      this.assetModal.showModal();
    },
    onEdit(id: number) {
      this.modalTitle = "Edit other income";
      this.showModal(id);
    },
    onView(id: number) {
      this.modalTitle = "View other income";
      this.showModal(id);
    },
    showModal(id: number) {
      const otherIncome = this.getOtherIncome.filter(p => p.id == id)[0];
      this.assetEditor.setValues(otherIncome, this.getRetirementYear());
      this.assetModal.showModal();
    },
    async onDelete(id: number) {
      await app.deleteOtherIncome(id);
    },
    onEditFinished() {
      this.assetModal.closeModal();
    },
    getRetirementYear(): number {
      return new Date(this.$store.state.retiree.retirementDate).getFullYear();
    }
  },
  computed: {
    getOtherIncome(): Array<OtherIncome> {
      return this.$store.state.retiree?.otherIncome ?? new Array<OtherIncome>();
    },
    hasOtherIncome(): boolean {
      return this.getOtherIncome.length > 0;
    }
  }
});
</script>
